import { useState } from 'react';
import { Container, Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/ReactToastify.css';
import Header from './components/Header';
import Links from './components/Links/Links';
import Weather from './components/Weather';
import HubCard from './components/HubCard';
import MediaCard from './components/MediaCard';
import FootballCard from './components/FootballCard';
import NowPlayingCard from './components/NowPlayingCard';
import NewsPanel from './components/NewsPanel';
import FlipPanel from './components/FlipPanel';
import MatchDayChip from './components/MatchDayChip';
import useUnitedFixtures from './components/useUnitedFixtures';

const Hub = () => {
  const [showSport, setShowSport] = useState(false);
  const fixtures = useUnitedFixtures();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Header />

      <Container maxWidth="lg" sx={{ py: 4, flex: 1 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '320px minmax(0, 1fr)' },
            gridTemplateAreas: {
              xs: `"weather" "links" "media" "flip"`,
              md: `"links right"`,
            },
            alignItems: 'start',
            gap: 3,
          }}
        >
          <Box sx={{ gridArea: 'links' }}>
            <HubCard>
              <Links />
            </HubCard>
          </Box>

          {/* Its own grid on desktop so expanding Links never resizes these
              cards; `contents` on mobile lets them interleave with Links. */}
          <Box
            sx={{
              gridArea: { md: 'right' },
              display: { xs: 'contents', md: 'grid' },
              gridTemplateColumns: { md: 'minmax(0, 1fr) minmax(0, 1fr)' },
              gridTemplateAreas: {
                md: `"weather weather" "media media" "flip flip"`,
              },
              gap: 3,
            }}
          >
            <Box sx={{ gridArea: 'weather' }}>
              <HubCard>
                <Weather />
              </HubCard>
            </Box>

            <Box sx={{ gridArea: 'media' }}>
              <HubCard>
                <MediaCard />
              </HubCard>
            </Box>

            <Box
              sx={{
                gridArea: 'flip',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
              }}
            >
              <ToggleButtonGroup
                exclusive
                size="small"
                value={showSport ? 'sport' : 'news'}
                onChange={(_, value) => {
                  // null when the active button is clicked again — keep the face.
                  if (value) setShowSport(value === 'sport');
                }}
                sx={{
                  alignSelf: 'flex-end',
                  gap: 0.75,
                  // Separate pills rather than MUI's joined segments.
                  '& .MuiToggleButtonGroup-grouped, & .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
                    ml: 0,
                    borderLeft: '1px solid',
                    borderLeftColor: 'divider',
                    borderRadius: 14,
                  },
                  '& .MuiToggleButton-root': {
                    px: 1.5,
                    py: 0.5,
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: 0.4,
                    borderRadius: 14,
                    border: '1px solid',
                    borderColor: 'divider',
                  },
                  '& .MuiToggleButtonGroup-grouped.Mui-selected': {
                    bgcolor: 'rgba(255,47,146,0.18)',
                    borderColor: 'rgba(255,47,146,0.55)',
                  },
                }}
              >
                <ToggleButton value="news">News</ToggleButton>
                <ToggleButton value="sport">
                  Sport &amp; Games
                  {fixtures.today && <MatchDayChip event={fixtures.today} />}
                </ToggleButton>
              </ToggleButtonGroup>

              <FlipPanel
                flipped={showSport}
                frontLabel="News headlines"
                backLabel="Man United and Now Playing"
                front={
                  <HubCard dense>
                    <NewsPanel />
                  </HubCard>
                }
                back={
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'minmax(0, 1fr)',
                        md: 'repeat(2, minmax(0, 1fr))',
                      },
                      gap: 3,
                    }}
                  >
                    <HubCard dense>
                      <FootballCard {...fixtures} />
                    </HubCard>
                    <HubCard dense>
                      <NowPlayingCard />
                    </HubCard>
                  </Box>
                }
              />
            </Box>
          </Box>
        </Box>
      </Container>

      <ToastContainer />
    </Box>
  );
};

export default Hub;
